import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Crown, Sparkles, BookOpen, Users, Zap } from 'lucide-react';
export function PSIGatheringSection() {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  return <section className="relative py-12 sm:py-16 md:py-20 px-4 sm:px-6 bg-gradient-to-b from-black via-purple-950/20 to-black overflow-hidden" data-id="element-3791">
      {/* Mystical background effects */}
      <div className="absolute inset-0 opacity-10" style={{
      backgroundImage: `radial-gradient(circle at 20% 50%, rgba(168, 85, 247, 0.3) 0%, transparent 50%),
                         radial-gradient(circle at 80% 50%, rgba(59, 130, 246, 0.3) 0%, transparent 50%)`
    }} data-id="element-3792" />

      {/* Glowing orbs */}
      <div className="absolute top-1/4 left-1/3 w-64 h-64 sm:w-96 sm:h-96 bg-purple-600/10 rounded-full blur-2xl sm:blur-3xl animate-pulse-slow" data-id="element-3793" />
      <div className="absolute bottom-1/4 right-1/3 w-64 h-64 sm:w-96 sm:h-96 bg-blue-600/10 rounded-full blur-2xl sm:blur-3xl animate-pulse-slow" style={{
      animationDelay: '1s'
    }} data-id="element-3794" />

      <motion.div ref={ref} initial={{
      opacity: 0,
      y: 30
    }} animate={inView ? {
      opacity: 1,
      y: 0
    } : {}} transition={{
      duration: 0.8
    }} className="relative z-10 max-w-4xl mx-auto" data-id="element-3795">
        {/* Decorative header */}
        <div className="flex items-center justify-center gap-4 mb-8 sm:mb-12" data-id="element-3796">
          <div className="w-16 sm:w-24 h-px bg-gradient-to-r from-transparent via-purple-500/50 to-purple-500/50" data-id="element-3797" />
          <Crown className="w-6 h-6 sm:w-8 sm:h-8 text-purple-500" data-id="element-3798" />
          <div className="w-16 sm:w-24 h-px bg-gradient-to-l from-transparent via-purple-500/50 to-purple-500/50" data-id="element-3799" />
        </div>

        {/* Main headline */}
        <h2 className="text-3xl sm:text-4xl md:text-6xl font-serif text-center mb-8 sm:mb-12 leading-tight text-stone-100" style={{
        fontFamily: 'Cinzel, serif',
        textShadow: '0 0 30px rgba(168, 85, 247, 0.4)'
      }} data-id="element-3800">
          At the{' '}
          <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-purple-500 bg-clip-text text-transparent" style={{
          textShadow: '0 0 40px rgba(168, 85, 247, 0.6)'
        }} data-id="element-3801">
            Peptide Science Institute
          </span>
          , We Are Doing What the Scholars at the Great Library of Alexandria
          Did:
        </h2>

        {/* Body content */}
        <div className="space-y-4 sm:space-y-6 text-base sm:text-lg md:text-xl leading-relaxed text-stone-300" data-id="element-3802">
          <p className="text-xl sm:text-2xl font-bold text-center text-purple-400" style={{
          textShadow: '0 0 30px rgba(168, 85, 247, 0.4)'
        }} data-id="element-3803">
            We are gathering the most vital, accurate, and powerful information
            about human biology and bringing it together.
          </p>

          <div className="bg-gradient-to-br from-purple-950/30 to-black border-l-4 border-purple-500 p-6 sm:p-8 my-6 sm:my-8" data-id="element-3804">
            <p className="text-stone-100 mb-4 text-base sm:text-lg" data-id="element-3805">
              Our team, spearheaded by{' '}
              <span className="font-bold text-purple-400" data-id="element-3806">Brenden Henry</span>{' '}
              and{' '}
              <span className="font-bold text-purple-400" data-id="element-3807">Dr. Ali Mazloum</span>
              , has been obsessively discovering, collecting and refining this
              "scattered" wisdom.
            </p>
          </div>

          {/* Brenden's achievements */}
          <div className="space-y-4 pl-4 sm:pl-6 border-l-2 border-cyan-500/30 text-base sm:text-lg" data-id="element-3808">
            <p className="text-cyan-400 font-bold" data-id="element-3809">
              Brenden discovered and translated many of the original Russian
              peptide research papers and brought them to the West for the first
              time in history.
            </p>
            <p data-id="element-3810">
              He has gone deeper into the workings of peptides in the human body
              than perhaps anyone else alive.
            </p>
          </div>

          {/* Team achievements */}
          <p className="text-base sm:text-lg" data-id="element-3811">
            And together with Dr. Ali and our team here at PSI, has spearheaded
            new, original research which unveiled the existence of{' '}
            <span className="font-bold text-purple-400" data-id="element-3812">
              THREE types of bioregulator peptides.
            </span>
          </p>

          <p className="text-stone-400 italic text-center text-sm sm:text-base" data-id="element-3813">
            (Along with a class of "super peptides" we've categorized as
            bioaugmentors.)
          </p>

          {/* Key value proposition */}
          <div className="bg-gradient-to-br from-purple-950/40 via-blue-950/30 to-black border-2 border-purple-500/50 p-6 sm:p-8 md:p-10 my-8 sm:my-12 relative" data-id="element-3814">
            <div className="absolute top-0 left-0 w-8 sm:w-10 h-8 sm:h-10 border-l-2 border-t-2 border-purple-500" data-id="element-3815" />
            <div className="absolute top-0 right-0 w-8 sm:w-10 h-8 sm:h-10 border-r-2 border-t-2 border-blue-500" data-id="element-3816" />
            <div className="absolute bottom-0 left-0 w-8 sm:w-10 h-8 sm:h-10 border-l-2 border-b-2 border-blue-500" data-id="element-3817" />
            <div className="absolute bottom-0 right-0 w-8 sm:w-10 h-8 sm:h-10 border-r-2 border-b-2 border-purple-500" data-id="element-3818" />

            <p className="text-lg sm:text-xl md:text-2xl font-bold text-center text-stone-100 leading-relaxed" data-id="element-3819">
              With our combined knowledge of peptides and the human body, our
              team has largely taken the{' '}
              <span className="text-purple-400" data-id="element-3820">
                guesswork out of biological regeneration, enhancement and
                optimization.
              </span>
            </p>
          </div>

          {/* Three key points */}
          <div className="space-y-4 sm:space-y-6 my-8 sm:my-12" data-id="element-3821">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="flex items-start gap-4 p-4 sm:p-6 bg-gradient-to-r from-purple-950/30 to-transparent border-l-4 border-purple-500 rounded-r-lg" data-id="element-3822">
              <Sparkles className="w-6 h-6 text-purple-400 flex-shrink-0 mt-1" data-id="element-3823" />
              <p className="text-stone-100 text-base sm:text-lg" data-id="element-3824">
                We've created a{' '}
                <span className="font-bold text-purple-400" data-id="element-3825">
                  CENTRALIZED source of truth.
                </span>
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="flex items-start gap-4 p-4 sm:p-6 bg-gradient-to-r from-blue-950/30 to-transparent border-l-4 border-blue-500 rounded-r-lg" data-id="element-3826">
              <Zap className="w-6 h-6 text-blue-400 flex-shrink-0 mt-1" data-id="element-3827" />
              <p className="text-stone-100 text-base sm:text-lg" data-id="element-3828">
                A{' '}
                <span className="font-bold text-blue-400" data-id="element-3829">
                  command center for biological mastery.
                </span>
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="flex items-start gap-4 p-4 sm:p-6 bg-gradient-to-r from-purple-950/30 to-transparent border-l-4 border-purple-500 rounded-r-lg" data-id="element-3830">
              <BookOpen className="w-6 h-6 text-purple-400 flex-shrink-0 mt-1" data-id="element-3831" />
              <p className="text-stone-100 text-base sm:text-lg" data-id="element-3832">
                A place where the scattered knowledge has been{' '}
                <span className="font-bold text-purple-400" data-id="element-3833">
                  gathered, organized, validated, and made accessible.
                </span>
              </p>
            </motion.div>
          </div>

          {/* Peptide Mastery reveal */}
          <p className="text-lg sm:text-xl font-bold text-stone-100 text-center" data-id="element-3834">
            And we've distilled much of this knowledge into the most
            comprehensive, scientifically validated, and in-depth education on
            peptides ever created:
          </p>

          <div className="bg-gradient-to-br from-amber-950/40 to-black border-2 border-amber-600 p-6 sm:p-8 md:p-10 my-6 sm:my-8 relative" data-id="element-3835">
            <div className="absolute top-0 left-0 w-6 sm:w-8 h-6 sm:h-8 border-l-2 border-t-2 border-amber-600" data-id="element-3836" />
            <div className="absolute top-0 right-0 w-6 sm:w-8 h-6 sm:h-8 border-r-2 border-t-2 border-amber-600" data-id="element-3837" />
            <div className="absolute bottom-0 left-0 w-6 sm:w-8 h-6 sm:h-8 border-l-2 border-b-2 border-amber-600" data-id="element-3838" />
            <div className="absolute bottom-0 right-0 w-6 sm:w-8 h-6 sm:h-8 border-r-2 border-b-2 border-amber-600" data-id="element-3839" />

            <p className="text-2xl sm:text-3xl md:text-4xl font-bold text-center text-amber-500 mb-4" style={{
            fontFamily: 'Cinzel, serif',
            textShadow: '0 0 40px rgba(217, 119, 6, 0.6)'
          }} data-id="element-3840">
              Peptide Mastery 2.0
            </p>

            <p className="text-lg sm:text-xl text-center text-stone-100" data-id="element-3841">
              Which is now being included{' '}
              <span className="font-bold text-green-400" data-id="element-3842">FREE</span> when you
              join{' '}
              <span className="font-bold text-purple-400" data-id="element-3843">
                PSI PRO — The Private Research Order.
              </span>
            </p>
          </div>

          {/* Closing emphasis */}
          <div className="text-center space-y-4 py-6 sm:py-8" data-id="element-3844"></div>
        </div>
      </motion.div>
    </section>;
}